import { fromFirstReading } from '@/components/charts/series';

/**
 * A compact line, drawn as SVG with no charting dependency.
 *
 * ## Hand-rolled rather than a library
 *
 * A sparkline is a polyline through normalised points. Pulling in a charting library for that
 * would add ~100 KB to a bundle Vite is already warning about, for something that is thirty lines
 * and gives exact control over the look.
 *
 * ## Gaps are breaks in the line, not zeroes
 *
 * A null point means the platform has no reading for that interval — an unreachable server, a
 * stopped collector, or the first sample where a rate cannot yet be derived. Interpolating across
 * it would draw a confident line through a period nobody observed, and dropping it to zero would
 * claim the machine was idle.
 *
 * So the path breaks. That is the same rule the monitoring buckets and the uptime percentage
 * follow, and it is the whole reason this component takes `(number | null)[]` rather than
 * `number[]`.
 */
export function Sparkline({
  values: raw,
  color,
  height = 40,
  /** Fixed upper bound, for percentages. Omit to scale to the data, for rates. */
  max,
  className,
}: {
  values: (number | null)[];
  /** A CSS colour. Callers pass the same hue as the metric's icon. */
  color: string;
  height?: number;
  // Explicitly `| undefined`: callers compute this conditionally and pass the result
  // straight through, which exactOptionalPropertyTypes distinguishes from omitting it.
  max?: number | undefined;
  className?: string | undefined;
}) {
  const values = fromFirstReading(raw);
  const points = values.length;

  if (points === 0 || values.every((value) => value === null)) {
    return (
      <div
        className={`flex items-center text-xs text-muted-foreground ${className ?? ''}`}
        style={{ height }}
      >
        No history yet
      </div>
    );
  }

  const width = 240;
  const known = values.filter((v): v is number => v !== null);

  // Scale to the data when no ceiling is given, with a floor of 1 so a flat line of zeroes does
  // not divide by zero and does not fill the whole box.
  const ceiling = max ?? Math.max(1, ...known);
  const step = points > 1 ? width / (points - 1) : 0;

  // Each contiguous run of known values becomes its own polyline. A single path with gaps would
  // need `M` commands mid-string; separate runs are easier to read and to reason about.
  const runs: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];

  values.forEach((value, index) => {
    if (value === null) {
      if (run.length > 0) {
        runs.push(run);
        run = [];
      }

      return;
    }

    const clamped = Math.max(0, Math.min(ceiling, value));
    run.push({
      x: index * step,
      // 2px of padding top and bottom so a value at the ceiling is not clipped by the viewBox.
      y: height - 2 - (clamped / ceiling) * (height - 4),
    });
  });

  if (run.length > 0) {
    runs.push(run);
  }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={`w-full ${className ?? ''}`}
      style={{ height }}
      aria-hidden
    >
      {runs.map((segment, index) =>
        segment.length === 1 ? (
          // A lone point between two gaps would be invisible as a polyline, so it is drawn as a
          // dot. Otherwise a server that reported once in an hour would look like it never did.
          <circle
            key={index}
            cx={segment[0].x}
            cy={segment[0].y}
            r={1.5}
            fill={color}
          />
        ) : (
          <polyline
            key={index}
            points={segment.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ),
      )}
    </svg>
  );
}
